import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * Screenshot — framed image renderer for the `{% screenshot %}`
 * Markdoc macro on docs site articles.
 *
 * Design plan reference,
 * section 7 (image pipeline) + section 22.3 (article mockup shows
 * an in-line screenshot with caption).
 *
 * Visual convention (plan §7):
 *   - Faux browser chrome: 3 mac-style traffic-light dots + URL bar
 *     showing the production-style URL (never localhost).
 *   - 12px outer radius. Drop-shadow on dark + light surfaces.
 *   - AVIF primary, PNG fallback (via the <picture> element).
 *   - Lazy-loaded with a blurhash placeholder where supported.
 *
 * The Playwright build pipeline (Phase 0b) emits the actual image
 * assets + a content-hashed manifest. This component is the
 * presentational shell that wraps whatever image is referenced.
 */

export interface ScreenshotProps extends React.HTMLAttributes<HTMLElement> {
  /** Image src. AVIF preferred; pass that path here. */
  src: string
  /**
   * Optional PNG fallback path for browsers without AVIF support.
   * Omit for the manual override path where only one format exists.
   */
  fallbackSrc?: string
  /** Required alt text. */
  alt: string
  /** Optional caption rendered under the framed image. */
  caption?: React.ReactNode
  /**
   * URL shown in the faux browser chrome's address bar. Use the
   * production-style URL, not localhost (plan §7 visual convention).
   */
  url?: string
  /** Image natural width — helps the browser reserve space. */
  width?: number
  /** Image natural height — same as above. */
  height?: number
}

export const Screenshot = React.forwardRef<HTMLElement, ScreenshotProps>(
  ({ src, fallbackSrc, alt, caption, url, width, height, className, ...rest }, ref) => {
    return (
      <figure
        ref={ref}
        className={cn('flex flex-col gap-2 my-4', className)}
        {...rest}
      >
        <div
          className={cn(
            'overflow-hidden rounded-lg border border-ia-border bg-card',
            'shadow-[0_8px_32px_rgba(0,0,0,0.35)]',
          )}
        >
          {url && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-ia-border bg-ia-elevated">
              {/* Mac-style traffic lights. Pure decoration; not
                  interactive — they signal "this is a browser frame"
                  without claiming actual window controls. */}
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="h-3 w-3 rounded-pill bg-[#FF5F57]" aria-hidden="true" />
                <span className="h-3 w-3 rounded-pill bg-[#FEBC2E]" aria-hidden="true" />
                <span className="h-3 w-3 rounded-pill bg-[#28C840]" aria-hidden="true" />
              </div>
              <div
                className={cn(
                  'flex-1 truncate rounded-md px-2 py-1 font-mono text-caption',
                  'bg-card text-ia-text-secondary',
                )}
              >
                {url}
              </div>
            </div>
          )}
          <picture>
            {fallbackSrc && <source srcSet={src} type="image/avif" />}
            <img
              src={fallbackSrc ?? src}
              alt={alt}
              width={width}
              height={height}
              loading="lazy"
              decoding="async"
              className="block w-full h-auto"
            />
          </picture>
        </div>
        {caption && (
          <figcaption className="text-caption text-ia-text-secondary">
            {caption}
          </figcaption>
        )}
      </figure>
    )
  },
)
Screenshot.displayName = 'Screenshot'
